/*!
 * sceneReadout.js — where am I in the piece?
 * © 2026 Rob Canning — GPLv3
 *
 * A score driven by next: hand-overs (cueNext.js / sequence.js) has no
 * playhead position to read: it is a chain of named steps fired by hand, and
 * when one of them misfires there is nothing on screen that says which one was
 * supposed to be running. This puts that on screen:
 *
 *     SCENE 7/20   12.4s
 *     third-movement
 *     ← second-movement      → coda
 *
 * The chain is read from the score itself — every `next:<name>` link between
 * cues — so nothing has to be authored for it. Click the panel to hide it;
 * `window.oscillaSceneReadout.toggle()` or Alt+S brings it back.
 *
 * A score in map mode (stations, route.js) has its route table for this —
 * where the piece is and a jump to any station — so there it stays away.
 */

import { handleCueTrigger } from "../cues/cueDispatcher.js";
import { chainFromLinks } from "./sceneChain.js";
import { onReset } from "./teardown.js";

const TAG = "[scene]";

let chain = null;            // Map canonical name -> { next, index }
let chainAlias = null;       // any name a step answers to -> its canonical name
let total = 0;
let current = null;          // { name, index, at (ms) }
let panel = null;
let hidden = false;
let collapsed = false;       // header only, or header + the section index
let ticker = 0;              // counts the current step's seconds while the panel shows it

// ── reading the chain out of the score ──────────────────────────────
// Every cue that hands over, and every cue handed to. A name is whatever
// next: says: a cue's uid, or an element's id (runNext resolves either).
const NEXT_RE = /\bnext\s*:\s*["'`]?([A-Za-z_][\w-]*)/g;
const UID_RE = /\buid\s*:\s*([A-Za-z_][\w-]*)/;

function dslOf(el) {
  const data = el.getAttribute("data-oscilla");
  if (data && data.includes("(")) return data;
  const id = el.getAttribute("id");
  return (id && id.includes("(")) ? id : null;
}

/**
 * Build name -> next from the score's next: links, numbered by
 * chainFromLinks (sceneChain.js).
 */
function buildChain(root) {
  const svg = root || document.querySelector("#scoreContainer svg") || document.querySelector("svg");
  if (!svg) return null;
  if (svg.querySelector('[data-oscilla*="station("]')) return null;   // map mode: the route table says it

  // A step answers to its uid: and its element id: both are collected
  const handovers = [];             // { names: [...], to }
  for (const el of svg.querySelectorAll("[data-oscilla], [id]")) {
    const dsl = dslOf(el);
    if (!dsl || !dsl.includes("next:")) continue;
    NEXT_RE.lastIndex = 0;
    let m, to = null;
    while ((m = NEXT_RE.exec(dsl))) to = m[1];      // last next: on a compound wins
    if (!to) continue;
    const names = [dsl.match(UID_RE)?.[1], el.getAttribute("id")].filter(Boolean);
    handovers.push({ names, to });
  }
  if (!handovers.length) return null;

  const { map, alias, total: steps } = chainFromLinks(handovers);
  total = steps;
  chainAlias = alias;
  return map;
}

/** Forget the chain so the next step rebuilds it (a new score, a re-scan). */
export function resetSceneReadout() {
  chain = null; chainAlias = null; current = null; total = 0;
  render();
}
onReset("project", resetSceneReadout);   // the scene chain is read again from the next score's next: links

// The seconds the current step has run, in the header: the one thing that
// changes between steps, so the timer writes that alone (rebuilding the panel
// would also throw away where the section list was scrolled to). It runs only
// while the panel shows a step.
function heldSeconds() {
  return current ? ((performance.now() - current.at) / 1000).toFixed(1) + "s" : "";
}
function syncTicker() {
  const run = !hidden && !!current && !!panel?.isConnected;
  if (run && !ticker) {
    ticker = setInterval(() => {
      const el = panel?.querySelector("[data-scene-held]");
      if (el) el.textContent = heldSeconds();
    }, 200);
  } else if (!run && ticker) {
    clearInterval(ticker);
    ticker = 0;
  }
}

// ── jumping ─────────────────────────────────────────────────────────
/**
 * Run a step now, from wherever the piece has got to.
 *
 * Whatever the sequence was still waiting on is dropped first: the hand-over
 * queue, the tdelay: timers, a zoom in flight. They belong to the section
 * being left, and sitting through the rest of its wait before the new section
 * starts is exactly the dead time this is here to skip.
 */
export function jumpToScene(name) {
  if (!name) return false;
  try { window.oscillaSequence?.stop?.(); } catch { /* no sequence running */ }
  const el = document.getElementById(name)
    || document.querySelector(`[data-oscilla*="uid:${String(name).replace(/["\\]/g, "\\$&")}"]`);
  const dsl = el?.getAttribute?.("data-oscilla")
    || (el?.id?.includes("(") ? el.id : null);
  if (!dsl) { console.warn(`${TAG} nothing to run for "${name}"`); return false; }
  handleCueTrigger(dsl, false, true, el);
  return true;
}

// ── the panel ───────────────────────────────────────────────────────
function ensurePanel() {
  if (panel?.isConnected || typeof document === "undefined" || !document.body) return panel;
  panel = document.createElement("div");
  panel.id = "oscilla-scene-readout";
  // Oscilla's own floating controls carry this: the DSL inspector listens for
  // pointerdown on the document in the capture phase and would otherwise take
  // a press meant for one of them as a press on the score behind it.
  panel.className = "oscilla-chrome";
  panel.style.cssText = [
    "position:fixed", "left:12px", "bottom:104px", "z-index:100020",
    "border-radius:6px", "border:1px solid #bbb",
    "background:rgba(248,248,248,.94)", "color:#333",
    "font:12px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace",
    "box-shadow:0 1px 4px rgba(0,0,0,.15)",
    "min-width:200px", "max-width:280px", "display:none", "user-select:none",
  ].join(";");
  // Acted on at pointerup, not click. The DSL inspector listens for
  // pointerdown on the document in the CAPTURE phase and calls
  // preventDefault() on anything outside its own panel — which suppresses the
  // click event that would have followed, so a click handler here never ran
  // while the inspector was open. pointerup nothing intercepts.
  //
  // The list scrolls, so a press that travelled is a scroll, not a choice.
  let pressed = null;
  panel.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
    pressed = { x: e.clientX, y: e.clientY, target: e.target };
  });
  panel.addEventListener("pointerup", (e) => {
    e.stopPropagation();
    const from = pressed;
    pressed = null;
    if (!from || Math.hypot(e.clientX - from.x, e.clientY - from.y) > 6) return;
    const row = e.target.closest?.("[data-scene]");
    if (row) { jumpToScene(row.getAttribute("data-scene")); return; }
    if (e.target.closest?.("[data-scene-head]")) { collapsed = !collapsed; render(); }
  });
  // Nothing underneath should see these either way.
  panel.addEventListener("click", e => e.stopPropagation());
  document.body.appendChild(panel);
  return panel;
}

const esc = s => String(s).replace(/[&<>"]/g, c =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function render() {
  const p = ensurePanel();
  if (!p) return;
  syncTicker();
  if (hidden || (!current && !chain)) { p.style.display = "none"; return; }
  const rows = [];
  if (chain && !collapsed) {
    for (const [name, step] of chain) {
      const here = name === current?.name;
      rows.push(
        `<div data-scene="${esc(name)}" title="jump to this section" style="` +
          `padding:1px 12px;cursor:pointer;white-space:nowrap;overflow:hidden;` +
          `text-overflow:ellipsis;${here ? "background:#e8eef5;font-weight:600;color:#123" : "color:#667"}">` +
          `<span style="color:#aaa">${String(step.index).padStart(2, " ")}</span> ${esc(name)}` +
        `</div>`);
    }
  }
  p.innerHTML =
    `<div data-scene-head="1" style="padding:7px 12px 5px;cursor:pointer;` +
        `border-bottom:${collapsed ? "none" : "1px solid #e0e0e0"}" ` +
        `title="click to ${collapsed ? "list the sections" : "collapse"} — Alt+S hides this">` +
      `<div style="color:#888;letter-spacing:.08em;font-size:10px">` +
        `SCENE ${current?.index ?? "–"}${total ? "/" + total : ""}` +
        `<span data-scene-held="1" style="float:right;color:#aaa">${heldSeconds()}</span></div>` +
      `<div style="font-size:13px;font-weight:600;margin-top:2px">` +
        `${current ? esc(current.name) : "<span style='color:#aaa;font-weight:400'>not started</span>"}</div>` +
    `</div>` +
    (rows.length ? `<div style="max-height:42vh;overflow:auto;padding:4px 0">${rows.join("")}</div>` : "");
  p.style.display = "block";
}

/**
 * A cue fired. Cheap on purpose: this is called for every cue on every
 * crossing, and a score has thousands. Two Set lookups and out.
 */
export function noteScene(uid, elementId) {
  if (chain === null) chain = buildChain() ?? false;   // false = nothing to show
  if (!chain) return;
  const name = (uid && chainAlias.get(uid)) || (elementId && chainAlias.get(elementId)) || null;
  if (!name || name === current?.name) return;
  current = { name, index: chain.get(name).index, at: performance.now() };
  hidden = false;                                      // a new step brings it back
  render();
}

export function toggleSceneReadout() {
  hidden = !hidden;
  render();
  return !hidden;
}

if (typeof window !== "undefined") {
  window.oscillaSceneReadout = {
    toggle: toggleSceneReadout,
    reset: resetSceneReadout,
    jump: jumpToScene,
    /** Show the index without waiting for the first step to fire. */
    show() { hidden = false; if (chain === null) chain = buildChain() ?? false; render(); return !!chain; },
    get current() { return current?.name ?? null; },
    get steps() { return chain ? [...chain.keys()] : []; },
  };
  window.addEventListener("keydown", (e) => {
    if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      toggleSceneReadout();
    }
  });
}
